// ============================================================
// THE DECISION TIMER - "you have a minute".
//
// The GM starts a countdown and every screen shows it. It is one record on
// the phase frame: how long, and WHEN IT STARTED by the GM's clock. Each
// screen counts down from that one timestamp with its own clock, so nothing
// is written while it runs - and two machines whose clocks disagree by a
// few seconds will show it a few seconds apart, which a table can live
// with. At zero it says "Time!" for a few seconds and is then let go of
// (`timerShows`); the GM may clear it sooner.
//
// It enforces nothing. It is pressure, not a rule.
//
// Pure. Zero React dependency.
// ============================================================

export interface DecisionTimer {
  /** How long it runs, in seconds. */
  seconds: number;
  /** When it was started, in ms, by the clock of whoever started it. */
  startedAt: number;
}

/** The lengths the GM is offered, in seconds. */
export const TIMER_LENGTHS = [30, 60, 120, 300] as const;

const MAX_SECONDS = 3600;

export function startTimer(seconds: number, now: number): DecisionTimer {
  return { seconds: Math.max(1, Math.min(MAX_SECONDS, Math.round(seconds))), startedAt: now };
}

/** A stored timer, validated; null when there is none or it is not one. */
export function readTimer(raw: unknown): DecisionTimer | null {
  const stored = (raw ?? {}) as { seconds?: unknown; startedAt?: unknown };
  const { seconds, startedAt } = stored;
  if (typeof seconds !== 'number' || typeof startedAt !== 'number') return null;
  if (!Number.isFinite(seconds) || !Number.isFinite(startedAt) || seconds <= 0) return null;
  return startTimer(seconds, startedAt);
}

/** Whole seconds left at `now`, never below zero - and never more than it was started with, whatever the clocks say. */
export function secondsLeft(timer: DecisionTimer, now: number): number {
  const left = Math.ceil(timer.seconds - (now - timer.startedAt) / 1000);
  return Math.max(0, Math.min(timer.seconds, left));
}

/** How long "Time!" stays on a screen once the count reaches zero, in ms. */
export const TIME_UP_SHOWS_MS = 8000;

/**
 * Is the timer still worth showing at `now`? While it counts, and for a few
 * seconds of "Time!" after. Then every screen lets go of it by itself: it
 * used to stay until the GM cleared it, from a button that is only on the
 * story pane - so over a fight or a stealth scene it stayed for good (user,
 * 2026-10-05).
 */
export function timerShows(timer: DecisionTimer, now: number): boolean {
  return now < timer.startedAt + timer.seconds * 1000 + TIME_UP_SHOWS_MS;
}

/** "1:05", "0:09" - minutes and seconds. */
export function clockText(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** A length as the GM's button says it: "30 s", "2 min". */
export const lengthLabel = (seconds: number): string => (seconds < 60 ? `${seconds} s` : `${seconds / 60} min`);
